import { useEffect, useMemo, useState, useCallback, useRef } from 'react';

interface BettingChipsProps {
  minBet: number;
  maxBet: number;
  bankroll: number;
  onPlaceBet: (amount: number) => void;
  disabled?: boolean;
  currentBetValue?: number;
  casinoName?: string;
}

interface ChipValue {
  value: number;
  color: string;
  textColor: string;
  pattern: string;
}

export default function BettingChips({
  minBet,
  maxBet,
  bankroll,
  onPlaceBet,
  disabled = false,
  currentBetValue = 0,
  casinoName = 'JK Casino'
}: BettingChipsProps) {
  // Initialize from the store-provided bet so a persisted bet (e.g. after a win)
  // survives this component remounting at round completion. Starting at 0 here
  // would let the local->store sync effect below fire onPlaceBet(0) on mount and
  // wipe the persisted bet before the store->local sync runs.
  const [currentBet, setCurrentBet] = useState(() => currentBetValue || 0);
  const [betHistory, setBetHistory] = useState<number[]>([]);
  const isSyncingFromStore = useRef(false);

  // Generate chip denominations based on minBet
  const getChipDenominations = (): ChipValue[] => {
    const chips: ChipValue[] = [];

    const baseValues = [1, 5, 25, 100, 500];
    const stripePattern = (stripe: string, fill: string) =>
      [
        // Inner cover: solid center, transparent ring so stripes only show on edge
        `radial-gradient(circle at center, ${fill} 0%, ${fill} 55%, transparent 55.5%)`,
        // Alternating edge stripes (20° on / 20° off) visible in outer ring
        //`repeating-conic-gradient(${stripe} 0deg 30deg, transparent 30deg 60deg)`,
        `repeating-conic-gradient(${stripe} 0deg 20deg, transparent 20deg 40deg)`,
        // Base fill for entire chip
        `radial-gradient(circle at center, ${fill} 0%, ${fill} 100%)`,
        // Soft highlight
        'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.12), transparent 48%)'
      ].join(', ');

    const colors = [
      { color: 'bg-white border-gray-400', textColor: 'text-gray-800', stripe: 'rgba(0,0,0,0.08)', fill: '#e5e7eb' },
      { color: 'bg-red-600 border-red-800', textColor: 'text-white', stripe: 'rgba(255,255,255,0.18)', fill: '#dc2626' },
      { color: 'bg-green-600 border-green-800', textColor: 'text-white', stripe: 'rgba(255,255,255,0.18)', fill: '#16a34a' },
      { color: 'bg-gray-800 border-gray-950', textColor: 'text-white', stripe: 'rgba(255,255,255,0.18)', fill: '#111827' },
      { color: 'bg-purple-600 border-purple-800', textColor: 'text-white', stripe: 'rgba(255,255,255,0.18)', fill: '#7c3aed' }
    ].map(c => ({ ...c, pattern: stripePattern(c.stripe, c.fill) }));

    const addChip = (value: number, index: number) => {
      if (value > maxBet) return;
      // Always allow $5 if within max, otherwise respect minBet threshold
      if (value !== 5 && value < minBet) return;
      if (chips.some(chip => chip.value === value)) return;
      chips.push({
        value,
        color: colors[index].color,
        textColor: colors[index].textColor,
        pattern: colors[index].pattern
      });
    };

    baseValues.forEach((value, index) => {
      addChip(value, index);
    });

    // Ensure the $5 chip exists when affordable
    if (!chips.some(c => c.value === 5) && 5 <= maxBet) {
      chips.push({
        value: 5,
        color: colors[1].color,
        textColor: colors[1].textColor,
        pattern: colors[1].pattern
      });
    }

    // If minBet is higher than base values, add custom chip at minBet
    if (chips.length === 0) {
      const customValue = minBet;
      chips.push({
        value: customValue,
        color: 'bg-blue-600 border-blue-800',
        textColor: 'text-white',
        pattern: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.12), transparent 45%), repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0, rgba(255,255,255,0.08) 6px, transparent 6px, transparent 12px)'
      });
    }

    return chips;
  };

  const chips = getChipDenominations();

  const buildStack = useCallback((amount: number) => {
    const values = chips.map(c => c.value).sort((a, b) => b - a);
    let remaining = Math.max(amount, 0);
    const stack: ChipValue[] = [];
    for (const v of values) {
      while (remaining >= v && stack.length < 20) {
        const chip = chips.find(c => c.value === v);
        if (!chip) break;
        stack.push(chip);
        remaining -= v;
      }
    }
    return stack;
  }, [chips]);

  // Sync bet to store while refunding previous via placeBet logic.
  // Only sync when NOT coming from store sync (prevents infinite loop) and only
  // when the local value actually diverges from the store value — otherwise a
  // remount would push the freshly-initialized value back and clobber a
  // persisted bet (e.g. the carried-over bet after a win).
  useEffect(() => {
    if (!isSyncingFromStore.current && currentBet !== currentBetValue) {
      onPlaceBet(currentBet);
    }
  }, [currentBet, currentBetValue, onPlaceBet]);

  // Keep local state in sync with store-provided bet (for persistence across hands)
  useEffect(() => {
    if (currentBetValue !== undefined && currentBetValue !== currentBet) {
      isSyncingFromStore.current = true;
      setCurrentBet(currentBetValue);
      setBetHistory([]);
      // Reset flag after state update
      requestAnimationFrame(() => {
        isSyncingFromStore.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBetValue]);

  const handleChipClick = (value: number) => {
    const newBet = currentBet + value;
    if (newBet <= maxBet && newBet <= bankroll) {
      setCurrentBet(newBet);
      setBetHistory((prev) => [...prev, value]);
    }
  };

  const handleClearBet = () => {
    setCurrentBet(0);
    setBetHistory([]);
  };

  const handleUndoBet = () => {
    setBetHistory((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setCurrentBet((bet) => Math.max(0, bet - last));
      return prev.slice(0, -1);
    });
  };

  // Build a chip stack representation for the current bet
  const betStack = useMemo(() => buildStack(currentBet), [buildStack, currentBet]);

  return (
    <div className="flex flex-col items-center justify-center gap-4 w-full">
      {/* Mobile: Horizontal layout with bet display and chips side by side */}
      <div className="flex md:hidden items-start gap-3 w-full justify-center">
        {/* Current Bet Display - Mobile */}
        <div className="flex flex-col gap-2 items-center">
          <div className="bg-gray-900/80 border-2 border-yellow-500 rounded-xl px-4 py-3 text-center shadow-xl min-w-[140px] flex-shrink-0">
            <div className="text-xs text-gray-300 mb-1">Current Bet</div>
            <div className="text-2xl font-bold text-yellow-400">{currentBet || 0}</div>
            {currentBet > 0 && currentBet < minBet && (
              <div className="text-xs text-red-400 mt-1">Min: {minBet}</div>
            )}
            {currentBet > maxBet && (
              <div className="text-xs text-red-400 mt-1">Max: {maxBet}</div>
            )}
            {betStack.length > 0 && (
              <div className="mt-2 flex flex-wrap justify-center gap-1">
                {betStack.map((chip, idx) => (
                  <div
                    key={`${chip.value}-${idx}`}
                    className={`relative w-5 h-5 rounded-full border-2 ${chip.color} ${chip.textColor} text-[10px] font-bold flex items-center justify-center shadow`}
                    style={{ backgroundImage: chip.pattern }}
                  >
                    <div className="absolute inset-[2px] rounded-full border border-white/30 opacity-60" />
                    <span className="relative">{chip.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Undo / Clear Buttons - Mobile (below current bet) */}
          <div className="w-full max-w-[180px] flex flex-col gap-2">
            <button
              onClick={handleUndoBet}
              disabled={disabled || betHistory.length === 0}
            className="relative overflow-hidden px-5 py-3 rounded-full font-bold text-base text-gray-800 shadow-lg transition-all transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #f9fafb, #e5e7eb, #d1d5db)',
              borderWidth: '3px',
              borderColor: 'rgba(156, 163, 175, 0.6)',
              color: '#1f2937',
              boxShadow: '0 2px 6px rgba(0,0,0,0.18)'
            }}
            >
            <div className="absolute inset-1 rounded-full border-2 border-dashed border-gray-400/30 pointer-events-none" />
            <span className="relative">Undo Last Bet</span>
            </button>
            <button
              onClick={handleClearBet}
              disabled={disabled || currentBet === 0}
            className="relative overflow-hidden px-5 py-3 rounded-full font-bold text-base text-gray-800 shadow-lg transition-all transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #f9fafb, #e5e7eb, #d1d5db)',
              borderWidth: '3px',
              borderColor: 'rgba(156, 163, 175, 0.6)',
              color: '#1f2937',
              boxShadow: '0 2px 6px rgba(0,0,0,0.18)'
            }}
            >
            <div className="absolute inset-1 rounded-full border-2 border-dashed border-gray-400/30 pointer-events-none" />
            <span className="relative">Clear Bet</span>
            </button>
          </div>
        </div>

        {/* Chip Buttons - Mobile 2x2 Grid */}
        <div className="grid grid-cols-2 gap-x-2 gap-y-3 py-2">
          {chips.map((chip) => (
            <button
              key={chip.value}
              onClick={() => handleChipClick(chip.value)}
              disabled={disabled || currentBet + chip.value > maxBet || currentBet + chip.value > bankroll}
              className={`
                relative w-20 h-20 rounded-full border-4
                ${chip.color}
                font-extrabold text-base
                ${chip.textColor}
                shadow-2xl overflow-hidden
                transform transition-all duration-150
                md:hover:scale-110 md:hover:shadow-[0_10px_25px_rgba(0,0,0,0.35)]
                active:scale-95
                disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
                flex items-center justify-center
              `}
              style={{
                backgroundImage: chip.pattern,
                boxShadow: disabled ? 'none' : '0 6px 12px rgba(0, 0, 0, 0.35), inset 0 -3px 6px rgba(0, 0, 0, 0.25)'
              }}
            >
              {/* Outer decorative borders */}
              <div className="absolute inset-[12px] rounded-full border-2 border-white/30 opacity-70" />
              <div className="absolute inset-[16px] rounded-full border border-white/20 opacity-70" />
              
              {/* Inner label disc - white center with casino branding */}
              <div className="absolute inset-3 rounded-full bg-white border border-gray-200 shadow-inner flex flex-col items-center justify-center">
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full -rotate-90">
                  <defs>
                    <path id={`circlePath-mobile-${chip.value}`} d="M 50,50 m -32,0 a 32,32 0 1,1 64,0 a 32,32 0 1,1 -64,0" />
                  </defs>
                  <text>
                    <textPath href={`#circlePath-mobile-${chip.value}`} startOffset="50%" textAnchor="middle" style={{ fontSize: '17px', fill: '#6b7280', fontFamily: '"Brush Script MT", cursive' }}>
                      {casinoName}
                    </textPath>
                  </text>
                </svg>
                <div className="text-xl leading-none font-black text-gray-800 relative z-10 mt-1">
                  {chip.value}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Desktop: Original layout */}
      <div className="hidden md:flex md:flex-row items-center justify-center gap-8 w-full">
        {/* Current Bet Display - Desktop */}
        <div className="bg-gray-900/80 border-2 border-yellow-500 rounded-xl px-5 py-4 text-center shadow-xl min-w-[200px]">
          <div className="text-xs text-gray-300 mb-1">Current Bet</div>
          <div className="text-3xl sm:text-4xl font-bold text-yellow-400">{currentBet || 0}</div>
          {currentBet > 0 && currentBet < minBet && (
            <div className="text-xs text-red-400 mt-1">Min: {minBet}</div>
          )}
          {currentBet > maxBet && (
            <div className="text-xs text-red-400 mt-1">Max: {maxBet}</div>
          )}
          {betStack.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-1">
              {betStack.map((chip, idx) => (
                <div
                  key={`${chip.value}-${idx}`}
                  className={`relative w-6 h-6 rounded-full border-2 ${chip.color} ${chip.textColor} text-[11px] font-bold flex items-center justify-center shadow`}
                  style={{ backgroundImage: chip.pattern }}
                >
                  <div className="absolute inset-[2px] rounded-full border border-white/30 opacity-60" />
                  <span className="relative">{chip.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chip Buttons - Desktop */}
        <div className="flex flex-wrap justify-center gap-4">
          {chips.map((chip) => (
            <button
              key={chip.value}
              onClick={() => handleChipClick(chip.value)}
              disabled={disabled || currentBet + chip.value > maxBet || currentBet + chip.value > bankroll}
              className={`
                relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full border-4
                ${chip.color}
                font-extrabold text-lg sm:text-xl
                ${chip.textColor}
                shadow-2xl overflow-hidden
                transform transition-all duration-150
                hover:scale-110 hover:shadow-[0_10px_25px_rgba(0,0,0,0.35)]
                active:scale-95
                disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
                flex items-center justify-center
              `}
              style={{
                backgroundImage: chip.pattern,
                boxShadow: disabled ? 'none' : '0 6px 12px rgba(0, 0, 0, 0.35), inset 0 -3px 6px rgba(0, 0, 0, 0.25)'
              }}
            >
              {/* Outer decorative borders */}
              <div className="absolute inset-[12px] sm:inset-[14px] lg:inset-[20px] rounded-full border-2 border-white/30 opacity-70" />
              <div className="absolute inset-[16px] sm:inset-[20px] lg:inset-[24px] rounded-full border border-white/20 opacity-70" />
              
              {/* Inner label disc - white center with casino branding */}
              <div className="absolute inset-3 sm:inset-4 rounded-full bg-white border border-gray-200 shadow-inner flex flex-col items-center justify-center">
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full -rotate-90">
                  <defs>
                    <path id={`circlePath-${chip.value}`} d="M 50,50 m -32,0 a 32,32 0 1,1 64,0 a 32,32 0 1,1 -64,0" />
                  </defs>
                  <text>
                    <textPath href={`#circlePath-${chip.value}`} startOffset="50%" textAnchor="middle" style={{ fontSize: '17px', fill: '#6b7280', fontFamily: '"Brush Script MT", cursive' }}>
                      {casinoName}
                    </textPath>
                  </text>
                </svg>
                <div className="text-xl sm:text-2xl leading-none font-black text-gray-800 relative z-10 mt-1">
                  {chip.value}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Undo / Clear Buttons - Desktop */}
        <div className="flex flex-col gap-2">
          <button
            onClick={handleUndoBet}
            disabled={disabled || betHistory.length === 0}
            className="relative overflow-hidden px-6 py-3 rounded-full font-bold text-base text-gray-800 shadow-lg transition-all transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #f9fafb, #e5e7eb, #d1d5db)',
              borderWidth: '3px',
              borderColor: 'rgba(156, 163, 175, 0.6)',
              color: '#1f2937',
              boxShadow: '0 2px 6px rgba(0,0,0,0.18)'
            }}
          >
            <div className="absolute inset-1 rounded-full border-2 border-dashed border-gray-400/30 pointer-events-none" />
            <span className="relative">Undo Last Bet</span>
          </button>
          <button
            onClick={handleClearBet}
            disabled={disabled || currentBet === 0}
            className="relative overflow-hidden px-6 py-3 rounded-full font-bold text-base text-gray-800 shadow-lg transition-all transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #f9fafb, #e5e7eb, #d1d5db)',
              borderWidth: '3px',
              borderColor: 'rgba(156, 163, 175, 0.6)',
              color: '#1f2937',
              boxShadow: '0 2px 6px rgba(0,0,0,0.18)'
            }}
          >
            <div className="absolute inset-1 rounded-full border-2 border-dashed border-gray-400/30 pointer-events-none" />
            <span className="relative">Clear Bet</span>
          </button>
        </div>
      </div>

    </div>
  );
}